// Multi-gauge sites (same site has both rainfall + river measurements) are rendered
// as MapLibre HTML Markers instead of symbol-layer icons. On hover the two icons
// animate apart so the user can click either one independently.

import { Marker } from "maplibre-gl";
import type { Map } from "maplibre-gl";

import type { RainfallTrend } from "../../../shared/types.ts";
import type { GaugeFeature, GaugeFeatureCollection } from "../api.ts";

import {
  RAINFALL_DRY_ICON, RAINFALL_LIGHT_ICON, RAINFALL_MODERATE_ICON, RAINFALL_HEAVY_ICON,
  riverIconSvg,
} from "./icons.ts";
import {
  ALERT_COLORS,
  RAIN_COLOR_DRY, RAIN_COLOR_LIGHT, RAIN_COLOR_MODERATE, RAIN_COLOR_HEAVY,
  RAIN_LIGHT, RAIN_MODERATE, RAIN_HEAVY,
} from "./map-colors.ts";

let _multiMarkers: Marker[] = [];

export function removeMultiGaugeMarkers(): void {
  _multiMarkers.forEach((m) => m.remove());
  _multiMarkers = [];
}

export function setMultiMarkersVisible(rainfall: boolean, river: boolean): void {
  _multiMarkers.forEach((m) => {
    const el = m.getElement();
    const rainEl = el.querySelector<HTMLElement>(".multi-marker__icon--rain");
    const riverEl = el.querySelector<HTMLElement>(".multi-marker__icon--river");
    if (rainEl)  rainEl.style.display  = rainfall ? "" : "none";
    if (riverEl) riverEl.style.display = river    ? "" : "none";
    el.style.display = (rainfall || river) ? "" : "none";
  });
}

function rainfallSvgForValue(value: number): string {
  if (value >= RAIN_HEAVY)    return RAINFALL_HEAVY_ICON;
  if (value >= RAIN_MODERATE) return RAINFALL_MODERATE_ICON;
  if (value >= RAIN_LIGHT)    return RAINFALL_LIGHT_ICON;
  return RAINFALL_DRY_ICON;
}

function rainfallColorForValue(value: number): string {
  if (value >= RAIN_HEAVY)    return RAIN_COLOR_HEAVY;
  if (value >= RAIN_MODERATE) return RAIN_COLOR_MODERATE;
  if (value >= RAIN_LIGHT)    return RAIN_COLOR_LIGHT;
  return RAIN_COLOR_DRY;
}

function riverAlertColor(alertLevel: string | null): string {
  return alertLevel === "high"  ? ALERT_COLORS.high!
    : alertLevel === "watch" ? ALERT_COLORS.watch!
    : ALERT_COLORS.normal!;
}

function trendArrowSvg(trend: RainfallTrend, color: string): string {
  const points = trend === "increasing" ? "7,1 1,13 13,13"
    : trend === "decreasing" ? "7,13 1,1 13,1"
    : "1,1 1,13 13,7";
  return `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10">`
    + `<polygon points="${points}" fill="${color}"/>`
    + `</svg>`;
}

function createMultiGaugeElement(
  rf: GaugeFeature,
  rif: GaugeFeature,
  onClick: (f: GaugeFeature) => void,
): HTMLElement {
  const container = document.createElement("div");
  container.className = "multi-marker";

  const rainDiv = document.createElement("div");
  rainDiv.className = "multi-marker__icon multi-marker__icon--rain";
  rainDiv.innerHTML = rainfallSvgForValue(rf.properties.value);
  if (rf.properties.trend) {
    const trendEl = document.createElement("div");
    trendEl.className = "multi-marker__trend";
    trendEl.innerHTML = trendArrowSvg(rf.properties.trend, rainfallColorForValue(rf.properties.value));
    rainDiv.appendChild(trendEl);
  }

  const riverDiv = document.createElement("div");
  riverDiv.className = "multi-marker__icon multi-marker__icon--river";
  riverDiv.innerHTML = riverIconSvg(riverAlertColor(rif.properties.alert_level));

  rainDiv.addEventListener("click", (e) => { e.stopPropagation(); onClick(rf); });
  riverDiv.addEventListener("click", (e) => { e.stopPropagation(); onClick(rif); });

  container.appendChild(rainDiv);
  container.appendChild(riverDiv);
  return container;
}

export function addMultiGaugeMarkers(
  map: Map,
  rainfall: GaugeFeatureCollection,
  river: GaugeFeatureCollection,
  onClick: (f: GaugeFeature) => void,
): void {
  const riverBySite = new globalThis.Map(river.features.map((f) => [f.properties.site_id, f]));

  for (const rf of rainfall.features) {
    if (!rf.properties.multi_gauge) continue;
    const rif = riverBySite.get(rf.properties.site_id);
    if (!rif) continue;
    const [lng, lat] = rf.geometry.coordinates as [number, number];
    const el = createMultiGaugeElement(rf, rif, onClick);
    _multiMarkers.push(new Marker({ element: el }).setLngLat([lng, lat]).addTo(map));
  }
}
